import * as React from "react"
import { EditorContent, EditorContext, mergeAttributes, Node, NodeViewContent, NodeViewWrapper, ReactNodeViewRenderer, useEditor } from "@tiptap/react"

import { StarterKit } from "@tiptap/starter-kit"
import { TextAlign } from "@tiptap/extension-text-align"
import { Typography } from "@tiptap/extension-typography"
import { Underline } from "@tiptap/extension-underline"
import { Box, Link, Toolbar } from "@mui/material"
import { EditorToolbar } from "./EditorToolbar"
import { Text } from "../Typography"
import Paragraph from "@tiptap/extension-paragraph"
import { RichTextInputProps } from "./interface"


export function RichTextInput(props: RichTextInputProps) {
  const content = props.value?.tiptap || props.value?.text;
  const showToolbar = !props.readOnly && !!content;

  const editor = useEditor({
    content,
    onUpdate: (p) => {
      const tiptap = p.editor.getJSON();
      const text = p.editor.getText();
      props.onChange?.({ text, tiptap, target: { value: text } });
    },
    onBlur: props.onBlur,
    onFocus: props.onFocus,
    immediatelyRender: true,
    editable: !props.readOnly,
    editorProps: {
      attributes: {
        autocomplete: "off",
        autocorrect: "off",
        autocapitalize: "off",
        "aria-label": "Main content area, start typing to enter text.",
      },
    },
    extensions: [
      buildExtendedParagraph(
        NodeViewContent,
        NodeViewWrapper,
        Paragraph,
        ReactNodeViewRenderer,
        mergeAttributes,
      ),
      buildLink(Node, NodeViewWrapper, ReactNodeViewRenderer, mergeAttributes),
      StarterKit.configure({
        paragraph: false,
        bulletList: { HTMLAttributes: { style: "margin: 0" }} }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Underline,
      Typography,
    ],
  });

  // Update our internal state if the external state doesn't match our internal state
  React.useEffect(() => {
    if (!content) {
      editor.commands.clearContent();
      return;
    }

    editor.commands.setContent(content);
  }, [JSON.stringify(content)]);

  return (
    <EditorContext.Provider value={{ editor }}>
      <Box className="content-wrapper" px="12px" flexGrow="1" pb="40px">
        <EditorContent
          editor={editor}
          role="presentation"
          className="simple-editor-content"
        />
      </Box>

      {showToolbar && (
        <Box position="absolute" bottom={0} left={0} right={0} zIndex={1}>
          <Toolbar sx={{ placeSelf: 'center', minHeight: '40px !important' }}>
            <EditorToolbar editor={editor} />
          </Toolbar>
        </Box>
      )}
    </EditorContext.Provider>
  )
}

function buildExtendedParagraph(
  NodeViewContent: any,
  NodeViewWrapper: any,
  Paragraph: any,
  ReactNodeViewRenderer: any,
  mergeAttributes: any,
) {
  return Paragraph.extend({
    renderHTML(props: any) {
      return ['p', mergeAttributes(props.HTMLAttributes), 0];
    },
    addNodeView() {
      return ReactNodeViewRenderer((props: any) => {
        const { textAlign } = props.node.attrs;

        return (
          <NodeViewWrapper>
            <Text size="m" textAlign={textAlign ?? 'left'}>
              <NodeViewContent />
            </Text>
          </NodeViewWrapper>
        );
      });
    },
  });
};

function buildLink(
  Node: any,
  NodeViewWrapper: any,
  ReactNodeViewRenderer: any,
  mergeAttributes: any,
) {
  return Node.create({
    name: 'medelyLink',
    group: 'inline',
    inline: true,
    addAttributes() {
      return {
        href: (element: any) => element.getAttribute('href'),
        text: (element: any) => element.getAttribute('text'),
      };
    },
    parseHTML() {
      return [
        {
          tag: 'link',
        },
      ];
    },
    renderHTML(props: any) {
      return ['link', mergeAttributes(props.HTMLAttributes), 0];
    },
    addNodeView() {
      return ReactNodeViewRenderer((props: any) => {
        const { href, text } = props.node.attrs ?? {};
        return (
          <NodeViewWrapper as="span">
            <Link href={href}>
              {text}
            </Link>
          </NodeViewWrapper>
        );
      });
    },
  });
};
